import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { SubscriptionBillingService } from '../../services/subscriptionBilling.service';
import { InstitutionService } from '../../services/institution.service';
import { ISubscriptionInvoice, InvoiceStatus, IInvoicePayment } from '../../interfaces/billing.interface';
import { IInstitution, IInstitutionResponse } from '../../interfaces/institution.interface';
import { useToast } from '../../context/ToastContext';
import InvoiceCreateDialog from './InvoiceCreateDialog';
import MarkPaidDialog from './MarkPaidDialog';

interface IInstitutionWithEmployeeCount extends IInstitution {
    employeeCount?: number;
}

const formatAmount = (amount: number): string => `BDT ${amount.toFixed(2)}`;

const invoiceStatusColor = (status: InvoiceStatus): 'default' | 'success' | 'warning' | 'error' => {
    switch (status) {
        case 'PAID':
            return 'success';
        case 'ISSUED':
            return 'warning';
        case 'CANCELLED':
            return 'error';
        default:
            return 'default';
    }
};

const paymentStatusColor = (status: IInvoicePayment['status']): 'default' | 'success' | 'warning' | 'error' => {
    switch (status) {
        case 'COMPLETED':
            return 'success';
        case 'FAILED':
            return 'error';
        case 'REFUNDED':
            return 'default';
        default:
            return 'warning';
    }
};

const isStuckBkashPayment = (payment: IInvoicePayment): boolean =>
    payment.method === 'BKASH_GATEWAY' && (payment.status === 'INITIATED' || payment.status === 'EXECUTING');

const InvoiceRow: React.FC<{
    invoice: ISubscriptionInvoice;
    institutionName: string;
    onIssue: (invoice: ISubscriptionInvoice) => void;
    onCancel: (invoice: ISubscriptionInvoice) => void;
    onMarkPaid: (invoice: ISubscriptionInvoice) => void;
    onVerifyNow: (payment: IInvoicePayment) => void;
    onMarkRefunded: (payment: IInvoicePayment) => void;
}> = ({ invoice, institutionName, onIssue, onCancel, onMarkPaid, onVerifyNow, onMarkRefunded }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <React.Fragment>
            <TableRow>
                <TableCell>
                    <IconButton
                        size="small"
                        aria-label="toggle payment history"
                        onClick={() => setExpanded(!expanded)}
                    >
                        {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                    {invoice.invoiceNumber}
                </TableCell>
                <TableCell>{institutionName}</TableCell>
                <TableCell>
                    {invoice.periodStart} — {invoice.periodEnd}
                </TableCell>
                <TableCell>{formatAmount(invoice.amount)}</TableCell>
                <TableCell>
                    <Chip label={invoice.status} color={invoiceStatusColor(invoice.status)} size="small" />
                </TableCell>
                <TableCell align="right">
                    {invoice.status === 'DRAFT' && (
                        <>
                            <Button size="small" onClick={() => onIssue(invoice)}>Issue</Button>
                            <Button size="small" color="error" onClick={() => onCancel(invoice)}>Cancel</Button>
                        </>
                    )}
                    {invoice.status === 'ISSUED' && (
                        <>
                            <Button size="small" onClick={() => onMarkPaid(invoice)}>Mark paid</Button>
                            <Button size="small" color="error" onClick={() => onCancel(invoice)}>Cancel</Button>
                        </>
                    )}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={6} sx={{ py: 0 }}>
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2 }}>
                            {invoice.payments.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">No payment attempts yet.</Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Method</TableCell>
                                            <TableCell>Amount</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Failure Reason</TableCell>
                                            <TableCell align="right">Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {invoice.payments.map(payment => (
                                            <TableRow key={payment.id}>
                                                <TableCell>{payment.method}</TableCell>
                                                <TableCell>{formatAmount(payment.amount)}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={payment.status}
                                                        color={paymentStatusColor(payment.status)}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>{payment.failureReason ?? '—'}</TableCell>
                                                <TableCell align="right">
                                                    {isStuckBkashPayment(payment) && (
                                                        <Button size="small" onClick={() => onVerifyNow(payment)}>
                                                            Verify now
                                                        </Button>
                                                    )}
                                                    {payment.status === 'COMPLETED' && (
                                                        <Button size="small" onClick={() => onMarkRefunded(payment)}>
                                                            Mark refunded
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
};

const OperatorBillingPage: React.FC = () => {
    const { showSuccess, showApiError } = useToast();

    const [invoices, setInvoices] = useState<ISubscriptionInvoice[]>([]);
    const [institutions, setInstitutions] = useState<IInstitutionWithEmployeeCount[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const [institutionFilter, setInstitutionFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [markPaidInvoice, setMarkPaidInvoice] = useState<ISubscriptionInvoice | null>(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const [invoicePage, institutionResponse] = await Promise.all([
                SubscriptionBillingService.getAllInvoices(),
                InstitutionService.getInstitutions(0, 100) as Promise<IInstitutionResponse>,
            ]);
            setInvoices(invoicePage.content);
            setInstitutions(institutionResponse.content as IInstitutionWithEmployeeCount[]);
        } catch (err) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const institutionNameById = (id: string): string =>
        institutions.find(inst => inst.id === id)?.name ?? id;

    const filteredInvoices = invoices.filter(invoice => {
        if (institutionFilter && invoice.institutionId !== institutionFilter) return false;
        if (statusFilter && invoice.status !== statusFilter) return false;
        return true;
    });

    const handleIssue = async (invoice: ISubscriptionInvoice) => {
        try {
            await SubscriptionBillingService.issueInvoice(invoice.id);
            showSuccess('Invoice issued');
            await loadData();
        } catch (err) {
            showApiError(err, 'Failed to issue invoice');
        }
    };

    const handleCancel = async (invoice: ISubscriptionInvoice) => {
        try {
            await SubscriptionBillingService.cancelInvoice(invoice.id);
            showSuccess('Invoice cancelled');
            await loadData();
        } catch (err) {
            showApiError(err, 'Failed to cancel invoice');
        }
    };

    const handleVerifyNow = async (payment: IInvoicePayment) => {
        try {
            await SubscriptionBillingService.resolvePayment(payment.id);
            showSuccess('Payment status refreshed');
            await loadData();
        } catch (err) {
            showApiError(err, 'Failed to verify payment');
        }
    };

    const handleMarkRefunded = async (payment: IInvoicePayment) => {
        try {
            await SubscriptionBillingService.markRefunded(payment.id);
            showSuccess('Payment marked as refunded');
            await loadData();
        } catch (err) {
            showApiError(err, 'Failed to mark payment as refunded');
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Subscription Billing</Typography>
                <Button variant="contained" onClick={() => setCreateDialogOpen(true)}>Create Invoice</Button>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <FormControl sx={{ minWidth: 220 }} size="small">
                    <InputLabel id="institution-filter-label">Institution</InputLabel>
                    <Select
                        labelId="institution-filter-label"
                        label="Institution"
                        value={institutionFilter}
                        onChange={(e) => setInstitutionFilter(e.target.value)}
                        inputProps={{ 'aria-label': 'Institution filter' }}
                    >
                        <MenuItem value="">All institutions</MenuItem>
                        {institutions.map(inst => (
                            <MenuItem key={inst.id} value={inst.id}>{inst.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 180 }} size="small">
                    <InputLabel id="status-filter-label">Status</InputLabel>
                    <Select
                        labelId="status-filter-label"
                        label="Status"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        inputProps={{ 'aria-label': 'Status filter' }}
                    >
                        <MenuItem value="">All statuses</MenuItem>
                        <MenuItem value="DRAFT">DRAFT</MenuItem>
                        <MenuItem value="ISSUED">ISSUED</MenuItem>
                        <MenuItem value="PAID">PAID</MenuItem>
                        <MenuItem value="CANCELLED">CANCELLED</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            )}

            {!loading && error && (
                <Typography color="error">Failed to load billing data. Please try again.</Typography>
            )}

            {!loading && !error && filteredInvoices.length === 0 && (
                <Typography color="text.secondary">No invoices found.</Typography>
            )}

            {!loading && !error && filteredInvoices.length > 0 && (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Invoice #</TableCell>
                                <TableCell>Institution</TableCell>
                                <TableCell>Period</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredInvoices.map(invoice => (
                                <InvoiceRow
                                    key={invoice.id}
                                    invoice={invoice}
                                    institutionName={institutionNameById(invoice.institutionId)}
                                    onIssue={handleIssue}
                                    onCancel={handleCancel}
                                    onMarkPaid={setMarkPaidInvoice}
                                    onVerifyNow={handleVerifyNow}
                                    onMarkRefunded={handleMarkRefunded}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <InvoiceCreateDialog
                open={createDialogOpen}
                institutions={institutions}
                onClose={() => setCreateDialogOpen(false)}
                onSuccess={loadData}
            />

            <MarkPaidDialog
                open={!!markPaidInvoice}
                invoice={markPaidInvoice}
                onClose={() => setMarkPaidInvoice(null)}
                onSuccess={loadData}
            />
        </Box>
    );
};

export default OperatorBillingPage;
